import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2Icon } from 'lucide-react';
import {
  Button,
  Combobox,
  ConfirmDialog,
  DataTable,
  Drawer,
  EmptyState,
  ErrorNotice,
  FactList,
  Field,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
  TextArea,
  TextInput,
  type ComboboxOption,
  type DataTableHeader,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { readMyIdentity } from '@/lib/events';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage } from '@/lib/use-list-query-state';
import {
  PAIRING_LIMITS,
  PROGRAM_OPTIONS_QUERY_KEY,
  PROGRAM_PAIRINGS_QUERY_KEY,
  createProgramPairing,
  listProgramOptions,
  listProgramPairings,
  pairingProgramName,
  removeProgramPairing,
  updateProgramPairing,
  type PairingProgram,
  type ProgramOption,
  type ProgramPairing,
  type ProgramPairingInput,
} from '@/lib/program-pairings';

const PAGE_SIZE = 25;

/**
 * Who may add, edit and remove pairings: the roles that may edit a program
 * (`PUT /programs/:id` is admin and superadmin). The API enforces the same.
 */
const PAIRING_EDITOR_ROLES: ReadonlySet<string> = new Set(['admin', 'superadmin']);

/** The filter above the table. */
const VIEW = { all: 'all', active: 'active', inactive: 'inactive' } as const;
type View = (typeof VIEW)[keyof typeof VIEW];

const SORT_FIELDS = { occasion: 'occasion', first: 'firstName', second: 'secondName', updated: 'updatedAt' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const HEADERS: readonly DataTableHeader[] = [
  { label: 'Occasion', sortKey: SORT_FIELDS.occasion },
  { label: 'First program', sortKey: SORT_FIELDS.first },
  { label: 'Second program', sortKey: SORT_FIELDS.second },
  { label: 'Updated', sortKey: SORT_FIELDS.updated },
];
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.occasion, direction: 'asc' };

/** Which drawer is open: none, a new pairing, or an existing one. */
type Open = { readonly kind: 'closed' } | { readonly kind: 'new' } | { readonly kind: 'pairing'; readonly id: string };
const CLOSED: Open = { kind: 'closed' };

function inView(pairing: ProgramPairing, view: View): boolean {
  if (view === VIEW.active) return pairing.isActive;
  if (view === VIEW.inactive) return !pairing.isActive;
  return true;
}

/** True when either program has left the catalog or is marked inactive there. */
function needsAttention(pairing: ProgramPairing): boolean {
  return [pairing.programA, pairing.programB].some((program) => program.name === null || !program.isActive);
}

/** `2026-09-29T12:00:00Z` → "Sep 29, 2026". */
function readableDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Catalog → Pairings (Roy's portal list, item 14: "Perfect pairings"): two
 * programs BCE recommends together for an occasion, and why. An active
 * pairing shows in client portals on both programs' pages and on Explore
 * programs, for clients who have both programs in their catalog. Each program
 * is still booked on its own.
 *
 * @returns The page.
 */
export function ProgramPairingsPage(): React.ReactElement {
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>(VIEW.all);
  const [open, setOpen] = useState<Open>(CLOSED);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const { page, setPage } = useUrlPage();

  const identity = useQuery({ queryKey: ['my-identity'], queryFn: ({ signal }) => readMyIdentity(signal), staleTime: 5 * 60_000 });
  const canEdit = PAIRING_EDITOR_ROLES.has(identity.data?.role ?? '');
  const pairings = useQuery({ queryKey: PROGRAM_PAIRINGS_QUERY_KEY, queryFn: ({ signal }) => listProgramPairings(signal) });

  const all = pairings.data ?? [];
  const counts: Readonly<Record<View, number>> = {
    [VIEW.all]: all.length,
    [VIEW.active]: all.filter((pairing) => pairing.isActive).length,
    [VIEW.inactive]: all.filter((pairing) => !pairing.isActive).length,
  };
  const needle = search.trim().toLowerCase();
  const rows = sortRows(
    all
      .filter((pairing) => inView(pairing, view))
      .filter(
        (pairing) =>
          needle === '' ||
          [pairing.occasion, pairing.reason, pairing.programA.name ?? '', pairing.programB.name ?? '']
            .join(' ')
            .toLowerCase()
            .includes(needle),
      )
      .map((pairing) => ({
        ...pairing,
        firstName: pairingProgramName(pairing.programA),
        secondName: pairingProgramName(pairing.programB),
      })),
    sort ?? DEFAULT_SORT,
  );
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const shown = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const openPairing = open.kind === 'pairing' ? (all.find((pairing) => pairing.id === open.id) ?? null) : null;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="CATALOG"
        title="Pairings"
        subtitle="Two programs BCE recommends together, and why. Clients see an active pairing when both programs are in their catalog."
        {...(canEdit ? { action: <Button guide="pairings-add" onClick={() => setOpen({ kind: 'new' })}>Add pairing</Button> } : {})}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search pairings"
          placeholder="An occasion, a program, a word from the reason"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-full max-w-sm"
        />
        <SegmentedControl
          label="Which pairings to show"
          options={[
            { key: VIEW.all, label: `All (${counts[VIEW.all]})` },
            { key: VIEW.active, label: `Active (${counts[VIEW.active]})` },
            { key: VIEW.inactive, label: `Inactive (${counts[VIEW.inactive]})` },
          ]}
          value={view}
          onChange={(next) => {
            setView(next);
            setPage(1);
          }}
        />
      </div>

      {pairings.isError ? (
        <ErrorNotice title="Couldn’t load pairings" message={errorMessage(pairings.error)} />
      ) : pairings.isPending ? (
        <SkeletonTable rows={5} columns={['w-[34%]', 'w-[24%]', 'w-[24%]', 'w-[12%]']} label="Loading pairings…" />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<Link2Icon aria-hidden className="size-6" />}
            title={all.length === 0 ? 'No pairings yet' : 'No pairings match'}
            message={
              all.length === 0
                ? 'A pairing puts two programs side by side for an occasion, like an afternoon team build before an evening game show, with a line on why they work together.'
                : 'Try a different search or show all pairings.'
            }
            {...(all.length === 0 && canEdit ? { action: <Button onClick={() => setOpen({ kind: 'new' })}>Add pairing</Button> } : {})}
          />
        </Panel>
      ) : (
        <>
          <DataTable guide="pairings-table" headers={HEADERS} {...(sort === undefined ? {} : { sort })} onSort={onSort} minWidth={860}>
            {shown.map((pairing, index) => (
              <tr key={pairing.id} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <span className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      data-guide={index === 0 ? 'pairings-open-first' : undefined}
                      onClick={() => setOpen({ kind: 'pairing', id: pairing.id })}
                      className="text-left font-bold text-brand underline-offset-2 hover:underline"
                    >
                      {pairing.occasion}
                    </button>
                    {pairing.isActive ? null : <Pill tone="neutral">Inactive</Pill>}
                    {needsAttention(pairing) ? <Pill tone="warning">Check programs</Pill> : null}
                  </span>
                  <span className="mt-0.5 block max-w-[420px] truncate text-portal-micro text-portal-muted">{pairing.reason}</span>
                </td>
                <ProgramCell program={pairing.programA} />
                <ProgramCell program={pairing.programB} />
                <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{readableDate(pairing.updatedAt)}</td>
              </tr>
            ))}
          </DataTable>
          {totalPages > 1 ? (
            <div className="mt-4">
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </div>
          ) : null}
        </>
      )}

      <PairingDrawer
        open={open}
        pairing={openPairing}
        canEdit={canEdit}
        onClose={() => setOpen(CLOSED)}
        onCreated={(id) => setOpen({ kind: 'pairing', id })}
      />
    </section>
  );
}

/** One side of a pairing in the table: the program over its label. */
function ProgramCell({ program }: { readonly program: PairingProgram }): React.ReactElement {
  return (
    <td className="p-3 text-portal-small">
      <span className={program.name === null ? 'block text-portal-muted italic' : 'block'}>{pairingProgramName(program)}</span>
      {program.label ? <span className="block text-portal-micro text-portal-muted">{program.label}</span> : null}
    </td>
  );
}

/**
 * The side panel: a new pairing, or one pairing to read, edit or remove.
 *
 * @param props - What is open, the pairing (when one is), and whether the viewer may edit.
 * @returns The drawer.
 */
function PairingDrawer({
  open,
  pairing,
  canEdit,
  onClose,
  onCreated,
}: {
  readonly open: Open;
  readonly pairing: ProgramPairing | null;
  readonly canEdit: boolean;
  readonly onClose: () => void;
  readonly onCreated: (id: string) => void;
}): React.ReactElement {
  const isNew = open.kind === 'new';
  const title = isNew ? 'Add a pairing' : (pairing?.occasion ?? 'Pairing');
  const subtitle = isNew
    ? 'Two programs for one occasion, and why they work together.'
    : pairing
      ? pairing.isActive
        ? 'Shown to clients with both programs in their catalog.'
        : 'Inactive: clients don’t see it.'
      : undefined;

  return (
    <Drawer open={isNew || pairing !== null} title={title} {...(subtitle ? { subtitle } : {})} onClose={onClose}>
      {/* Keyed so the form starts from the pairing it is showing. */}
      {isNew && canEdit ? (
        <PairingForm key="new" pairing={null} onDone={onClose} onCreated={onCreated} />
      ) : pairing === null ? null : canEdit ? (
        <PairingForm key={pairing.id} pairing={pairing} onDone={onClose} onCreated={onCreated} />
      ) : (
        <PairingFacts pairing={pairing} />
      )}
    </Drawer>
  );
}

/** A pairing for readers who may not edit it. */
function PairingFacts({ pairing }: { readonly pairing: ProgramPairing }): React.ReactElement {
  const side = (program: PairingProgram) => (program.label ? `${pairingProgramName(program)} (${program.label})` : pairingProgramName(program));
  return (
    <div className="space-y-5">
      <FactList
        columns={2}
        facts={[
          { label: 'First program', value: side(pairing.programA) },
          { label: 'Second program', value: side(pairing.programB) },
          { label: 'Shown to clients', value: pairing.isActive ? 'Yes' : 'No' },
          { label: 'Updated', value: readableDate(pairing.updatedAt) },
        ]}
      />
      <div>
        <p className="text-portal-micro font-bold text-portal-muted uppercase">Why it works</p>
        <p className="mt-1 text-portal-small leading-[1.6] whitespace-pre-line">{pairing.reason}</p>
      </div>
    </div>
  );
}

/** Picker options, keeping a stored program that has since left the catalog. */
function optionsFor(programs: readonly ProgramOption[], current: PairingProgram | null): ComboboxOption[] {
  const options: ComboboxOption[] = programs.map((program) => ({
    value: program.id,
    label: program.isActive ? program.name : `${program.name} (inactive)`,
  }));
  if (current && !programs.some((program) => program.id === current.id)) {
    options.unshift({ value: current.id, label: pairingProgramName(current) });
  }
  return options;
}

/**
 * Adding or editing a pairing. Saving an edit keeps the drawer open on the
 * saved pairing; adding one opens the new pairing, so the form becomes its
 * confirmation either way.
 */
function PairingForm({
  pairing,
  onDone,
  onCreated,
}: {
  readonly pairing: ProgramPairing | null;
  readonly onDone: () => void;
  readonly onCreated: (id: string) => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [occasion, setOccasion] = useState(pairing?.occasion ?? '');
  const [programAId, setProgramAId] = useState(pairing?.programA.id ?? '');
  const [programALabel, setProgramALabel] = useState(pairing?.programA.label ?? '');
  const [programBId, setProgramBId] = useState(pairing?.programB.id ?? '');
  const [programBLabel, setProgramBLabel] = useState(pairing?.programB.label ?? '');
  const [reason, setReason] = useState(pairing?.reason ?? '');
  const [isActive, setIsActive] = useState(pairing?.isActive ?? true);
  const [saved, setSaved] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const programs = useQuery({
    queryKey: PROGRAM_OPTIONS_QUERY_KEY,
    queryFn: ({ signal }) => listProgramOptions(signal),
    staleTime: 5 * 60_000,
  });

  const input: ProgramPairingInput = {
    occasion: occasion.trim(),
    programAId,
    programALabel: programALabel.trim(),
    programBId,
    programBLabel: programBLabel.trim(),
    reason: reason.trim(),
    isActive,
  };
  const samePrograms = programAId !== '' && programAId === programBId;
  const complete = input.occasion !== '' && programAId !== '' && programBId !== '' && input.reason !== '' && !samePrograms;
  const changed =
    pairing === null ||
    input.occasion !== pairing.occasion ||
    programAId !== pairing.programA.id ||
    input.programALabel !== pairing.programA.label ||
    programBId !== pairing.programB.id ||
    input.programBLabel !== pairing.programB.label ||
    input.reason !== pairing.reason ||
    isActive !== pairing.isActive;

  const refresh = () => queryClient.invalidateQueries({ queryKey: PROGRAM_PAIRINGS_QUERY_KEY });
  const save = useMutation({
    mutationFn: () => (pairing === null ? createProgramPairing(input) : updateProgramPairing(pairing.id, input)),
    onSuccess: async (result) => {
      await refresh();
      if (pairing === null) onCreated(result.id);
      else setSaved(true);
    },
  });
  const remove = useMutation({
    mutationFn: () => (pairing === null ? Promise.resolve() : removeProgramPairing(pairing.id)),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });

  const edit = <T,>(setter: (value: T) => void) => (value: T) => {
    setSaved(false);
    setter(value);
  };
  const options = programs.data ?? [];

  return (
    <div className="space-y-5">
      <Field guide="pairing-occasion" label="Occasion" htmlFor="pairing-occasion" required hint="The event it suits, in a client’s words.">
        <TextInput
          id="pairing-occasion"
          maxLength={PAIRING_LIMITS.occasion}
          value={occasion}
          onChange={(event) => edit(setOccasion)(event.target.value)}
        />
      </Field>

      <div data-guide="pairing-programs" className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_150px]">
        <Field label="First program" htmlFor="pairing-program-a" required>
          <Combobox
            id="pairing-program-a"
            options={optionsFor(options, pairing?.programA ?? null)}
            value={programAId}
            placeholder={programs.isPending ? 'Loading programs…' : 'Search programs…'}
            disabled={programs.isPending}
            onChange={edit(setProgramAId)}
          />
        </Field>
        <Field guide="pairing-when" label="When" htmlFor="pairing-label-a">
          <TextInput
            id="pairing-label-a"
            placeholder="Afternoon"
            maxLength={PAIRING_LIMITS.label}
            value={programALabel}
            onChange={(event) => edit(setProgramALabel)(event.target.value)}
          />
        </Field>
        <Field guide="pairing-second-program" label="Second program" htmlFor="pairing-program-b" required>
          <Combobox
            id="pairing-program-b"
            options={optionsFor(options, pairing?.programB ?? null)}
            value={programBId}
            placeholder={programs.isPending ? 'Loading programs…' : 'Search programs…'}
            disabled={programs.isPending}
            onChange={edit(setProgramBId)}
          />
        </Field>
        <Field guide="pairing-second-when" label="When" htmlFor="pairing-label-b">
          <TextInput
            id="pairing-label-b"
            placeholder="Evening"
            maxLength={PAIRING_LIMITS.label}
            value={programBLabel}
            onChange={(event) => edit(setProgramBLabel)(event.target.value)}
          />
        </Field>
      </div>
      <p className="-mt-1 text-portal-micro text-portal-muted">
        Leave “When” blank if the order doesn’t matter. Clients book each program on its own.
      </p>
      {samePrograms ? <p className="text-portal-micro font-bold text-rose-600">Choose two different programs.</p> : null}
      {programs.isError ? <ErrorNotice inline title="Couldn’t load programs" message={errorMessage(programs.error)} /> : null}

      <Field guide="pairing-reason" label="Why it works" htmlFor="pairing-reason" required hint="One or two sentences. Clients read this beside both programs.">
        <TextArea
          id="pairing-reason"
          rows={4}
          maxLength={PAIRING_LIMITS.reason}
          value={reason}
          onChange={(event) => edit(setReason)(event.target.value)}
        />
      </Field>

      <div data-guide="pairing-active">
        <label htmlFor="pairing-active" className="flex items-center gap-2 text-portal-small">
          <input
            id="pairing-active"
            type="checkbox"
            checked={isActive}
            onChange={(event) => edit(setIsActive)(event.target.checked)}
            className="size-4 accent-brand"
          />
          Show to clients
        </label>
        <p className="mt-1 text-portal-micro text-portal-muted">Untick to keep the pairing here without clients seeing it.</p>
      </div>

      {save.isError ? <ErrorNotice inline title="Pairing not saved" message={errorMessage(save.error)} /> : null}

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-portal-line pt-5">
        {saved && !changed ? <span className="mr-auto text-portal-micro font-bold text-emerald-600">Saved</span> : null}
        <Button variant="secondary" onClick={onDone}>
          {pairing === null ? 'Cancel' : 'Close'}
        </Button>
        <Button guide="pairing-submit" disabled={!complete || !changed || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : pairing === null ? 'Add pairing' : 'Save changes'}
        </Button>
      </div>

      {pairing === null ? null : (
        <div className="border-t border-portal-line pt-5">
          <Button variant="secondary" onClick={() => setConfirmRemove(true)}>
            Remove pairing
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmRemove}
        title="Remove this pairing?"
        message="Clients stop seeing it. To hide it for now and keep it here, untick Show to clients instead."
        confirmLabel="Remove"
        tone="danger"
        pending={remove.isPending}
        {...(remove.isError ? { error: errorMessage(remove.error) } : {})}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}
